import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuthContext } from '@/components/AuthProvider';

interface DemoModeContextType {
  /** Modo demonstração efetivo: só liga para quem tem acesso. */
  isDemoMode: boolean;
  /** Se o usuário atual pode usar o modo demonstração. */
  canUseDemoMode: boolean;
  toggleDemoMode: () => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

const DEMO_MODE_STORAGE_KEY = 'demo-mode-enabled';
const DEMO_MODE_ALLOWED_EMAIL = 'alex@musa.co';

export const DemoModeProvider = ({ children }: { children: ReactNode }) => {
  const { profile } = useAuthContext();
  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(DEMO_MODE_STORAGE_KEY);
    return stored === 'true';
  });

  const canUseDemoMode =
    profile?.is_internal === true || profile?.email === DEMO_MODE_ALLOWED_EMAIL;

  // Derivado no render: quem perde o acesso nunca vê dado mascarado, nem por
  // um frame. Antes isso era corrigido por efeito depois da primeira pintura.
  const isDemoMode = isEnabled && canUseDemoMode;

  useEffect(() => {
    // Persist to localStorage whenever it changes
    localStorage.setItem(DEMO_MODE_STORAGE_KEY, String(isEnabled));
  }, [isEnabled]);

  const toggleDemoMode = useCallback(() => {
    if (!canUseDemoMode) return;
    setIsEnabled((prev) => !prev);
  }, [canUseDemoMode]);

  const value = useMemo(
    () => ({ isDemoMode, canUseDemoMode, toggleDemoMode }),
    [isDemoMode, canUseDemoMode, toggleDemoMode],
  );

  return (
    <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>
  );
};

export const useDemoMode = () => {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
};
